import { useEffect, useState } from "react";
import { siteRoutes } from "utils/helpers/enums/routes";
import ReviewModal from "./ReviewModal";
import { useNavigate } from "react-router-dom";
import AttendeeBreadcrumb, {
  AttendeBreadcrumbLink,
} from "components/breadcrumbs/AttendeeBreadcrumb";
import useUtils from "hooks/useUtils";
import useHost from "containers/host/useHooks";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { ReviewFilterDTO } from "utils/helpers/models/attendee/review-filter-dto";
import useAttendee from "containers/attendee/useHooks";
import { TiStarFullOutline, TiStarHalfOutline } from "react-icons/ti";
import { useSelector } from "react-redux";
import WorkshopSessionPopup from "components/popups/workshop-session";
import ExperienceDetailHeader from "components/experience-detail-header";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import reviewsNotFoundGif from "assets/images/gifs/reviews-not-found.gif";
import useStore from "hooks/useStore";

const Ratings: React.FC = () => {
  const navigate = useNavigate();
  const { getQueryParams } = useUtils();
  const { getEventById } = useHost();
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  const params = getQueryParams();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const { getEventsReviews } = useAttendee();
  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [data, setData] = useState<any[]>([]);
  const [modalData, setModalData] = useState<{
    eventId?: number;
    hostId?: number;
  }>({});
  const { isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const openModal = () => {
    setModalData({
      eventId: event?.id,
      hostId: event?.host?.id,
    });
    setModalOpen(true);
  };
  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "Rating", path: siteRoutes.ratings },
  ];

  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, !data?.length]);

  const getEventDataByID = async () => {
    if (params?.id) {
      await getEventById(params?.id, setEvent);
    }
  };

  useEffect(() => {
    getEventDataByID();
  }, []);

  const goToHostDetail = () => {
    navigate(`${siteRoutes.attendeeHostProfileDetail}?id=${event?.host?.id}`);
  };

  // hide buuton on the base of startTime
  const lastSession = event.workshopSessions[event.workshopSessions.length - 1];
  const lastSessionDateTime = new Date(
    `${lastSession.startDateWithMonthAndYear} ${lastSession.endTime}`
  );
  const hasStarted = new Date() >= lastSessionDateTime;

  const visibleSessions = event.workshopSessions.slice(0, 3);
  const hasMoreSessions = event.workshopSessions.length > 3;

  const EventID = event?.id;

  const [filters, setFilters] = useState<Partial<ReviewFilterDTO>>({
    page: 1,
    totalPages: 1,
    size: 15,
    eventId: EventID,
  });
  const getAllReviews = async (filters: Partial<ReviewFilterDTO>) => {
    if (!filters.eventId) return;
    const isFirstPage = filters.page === 1;
    await getEventsReviews(
      setData,
      { ...filters, page: filters.page - 1 },
      isFirstPage ? [] : data,
      setFilters
    );
    setFilters({ ...filters });
  };

  useEffect(() => {
    if (EventID) {
      getAllReviews({ ...filters, eventId: EventID });
    }
  }, [EventID]);

  return (
    <div>
      <ExperienceDetailHeader
        event={event}
        breadcrumb={<AttendeeBreadcrumb links={breadcrumbLinks} />}
        onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="host-bg-gradient"
      />
      <div className="container">
        <div className="flex justify-between items-center gap-4 flex-wrap mt-6">
          <h2 className="text-2xl font-semibold">Reviews</h2>
          {!event?.alreadyReviewed && (
            <button
              onClick={openModal}
              className="px-4 py-2  rounded-full text-white bg-primaryBlueBg cursor-pointer"
            >
              Write a Review
            </button>
          )}
        </div>
        <div className="my-4">
          <div className="border-[0.3px] border-labelTextColor min-h-[40vh] p-6  rounded-lg shadow-sm flex flex-col gap-10">
            {data?.map((review: any) => (
              <div
                key={review.id}
                className="flex md:flex-nowrap flex-wrap items-start justify-between "
              >
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex items-center gap-2">
                    <img
                      src={concatPathWithBackendUrl(
                        review?.user?.profilePic?.fileDownloadUri
                      )}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <h4 className="font-semibold text-black text-lg">
                        {review.user.firstName} {review.user.lastName}
                      </h4>
                      <p className="text-sm text-labelTextColor">
                        <p className="text-[10px] text-warmGray">
                          {" "}
                          {review?.createdAt
                            ? new Date(review.createdAt)
                                .toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "numeric",
                                  minute: "numeric",
                                  hour12: true,
                                })
                                .replace(",", " -")
                            : "N/A"}
                        </p>
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-blackie">{review.comment}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 md:gap-3 md:items-end md:px-6 mt-3">
                  <div className="flex items-center space-x-2">
                    {[...Array(5)].map((_, i) => {
                      if (
                        review.rating % 1 !== 0 &&
                        i === Math.floor(review.rating)
                      ) {
                        return (
                          <div key={i} className="relative w-4 h-4">
                            <TiStarFullOutline className="absolute w-4 h-4 text-gray-300" />
                            <TiStarHalfOutline className="absolute w-4 h-4 text-yellow-500" />
                          </div>
                        );
                      }
                      return (
                        <TiStarFullOutline
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating
                              ? "text-yellow-500"
                              : "text-gray-300"
                          }`}
                        />
                      );
                    })}
                    <span className="ml-2 text-labelTextColor">
                      ({review.rating})
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <div>
              <CustomMessageDisplay
                show={!isLoading && !isInitialLoad && !data.length}
                title="No Reviews Yet"
                slogan="Be the first to share your experience! Your review helps others discover great events."
                image={reviewsNotFoundGif}
                imageAlt="No reviews found"
                className="py-16"
                imageClassName="w-[150px] h-[120px]"
                titleClassName="text-2xl font-bold text-blackie"
                sloganClassName="text-gray-500 text-base"
              />
            </div>
          </div>
          {filters.totalPages > filters.page && (
            <div className="text-center mt-10">
              <button
                className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                onClick={() =>
                  getAllReviews({ ...filters, page: filters.page + 1 })
                }
              >
                See More
              </button>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <ReviewModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          eventId={modalData.eventId}
          hostId={modalData.hostId}
          onReviewSubmitted={() => {
            // Refresh event data to update alreadyReviewed flag
            getEventDataByID();
            // Reload reviews to show the new one
            if (EventID) {
              setData([]); // Clear existing reviews
              getAllReviews({ ...filters, page: 1, eventId: EventID });
            }
          }}
        />
      )}

      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={event.workshopSessions}
        />
      )}
    </div>
  );
};

export default Ratings;
